import { useState, useEffect } from 'react';
import { Package, Loader2, Save, RotateCcw } from 'lucide-react';
import { apiCall } from './useAutoSave';
import { fmtCena } from './receptUtils';

const KANALY = [
    { id: 'bistro', label: 'Bistro' },
    { id: 'rozvoz', label: 'Rozvoz' },
    { id: 'vydej',  label: 'Výdej' },
];

/**
 * Balné produktu — poplatek za balení za kus, který pokladna připočte na
 * účet jedním řádkem „Balné". Výchozí částku dává balicí předpis (Balírna),
 * produkt si ji tady může pro kanál přepsat. Prázdné pole = podle předpisu.
 */
export default function ReceptPoplatekBaleni({ receptId, isDark, onNavigate }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [uklada, setUklada] = useState(false);
    const [chyba, setChyba] = useState('');

    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const prevzit = (d) => {
        setData(d);
        setForm(Object.fromEntries(KANALY.map(k => [k.id, d.kanaly[k.id]?.vlastni != null ? String(d.kanaly[k.id].vlastni) : ''])));
    };

    useEffect(() => {
        setData(null); setChyba('');
        apiCall(`/api/recepty/recepty/${receptId}/poplatek-baleni`).then(prevzit).catch(() => setChyba('Balné se nepodařilo načíst.'));
    }, [receptId]);

    if (!data) {
        return chyba ? <p className="text-xs mt-4" style={{ color: '#EF4444' }}>{chyba}</p> : null;
    }

    const zmeneno = KANALY.some(k => (form[k.id] ?? '') !== (data.kanaly[k.id]?.vlastni != null ? String(data.kanaly[k.id].vlastni) : ''));

    const ulozit = async () => {
        setUklada(true); setChyba('');
        try {
            const kanaly = Object.fromEntries(KANALY.map(k => [k.id, form[k.id] === '' ? null : Number(form[k.id])]));
            prevzit(await apiCall(`/api/recepty/recepty/${receptId}/poplatek-baleni`, 'PUT', { kanaly }));
        } catch (e) {
            setChyba(e?.message || 'Uložení selhalo.');
        } finally {
            setUklada(false);
        }
    };

    const inputStyle = {
        width: 80, textAlign: 'right', padding: '5px 8px', fontSize: 13, borderRadius: 8,
        border: `1px solid ${borderColor}`, background: 'transparent', color: 'var(--text-primary)',
    };

    return (
        <div className="rounded-xl mt-5" style={{ border: `1px solid ${borderColor}` }}>
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: `1px solid ${borderColor}` }}>
                <Package size={14} style={{ color: secondaryColor }} />
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Balné</span>
                <span className="text-[11px] flex-1" style={{ color: mutedColor }}>
                    poplatek za balení za kus — na účtu jako jeden řádek, do kuchyně nejde
                </span>
            </div>
            <div className="p-3">
                {!data.predpis && (
                    <p className="text-[11px] mb-2" style={{ color: mutedColor }}>
                        Produkt nemá balicí předpis — balné se účtuje jen tam, kde ho tady vyplníš.
                    </p>
                )}
                {KANALY.map(k => {
                    const kd = data.kanaly[k.id] || {};
                    return (
                        <div key={k.id} className="flex items-center gap-3 py-1.5">
                            <span className="text-xs font-semibold" style={{ width: 70, color: 'var(--text-primary)' }}>{k.label}</span>
                            <span className="text-[11px] flex-1" style={{ color: mutedColor }}>
                                {data.predpis
                                    ? <>předpis: {kd.predpis != null ? fmtCena(kd.predpis) : 'bez balného'}</>
                                    : null}
                            </span>
                            <input type="number" min="0" step="0.5" inputMode="decimal"
                                value={form[k.id] ?? ''}
                                placeholder={kd.predpis != null ? String(kd.predpis) : '—'}
                                onChange={e => setForm(f => ({ ...f, [k.id]: e.target.value }))}
                                style={inputStyle} />
                            <span className="text-[11px]" style={{ width: 34, color: mutedColor }}>Kč/ks</span>
                            {(form[k.id] ?? '') !== '' && (
                                <button title="Vrátit na předpis" onClick={() => setForm(f => ({ ...f, [k.id]: '' }))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                                    <RotateCcw size={12} />
                                </button>
                            )}
                        </div>
                    );
                })}
                <div className="flex items-center gap-3 mt-2">
                    {data.predpis && onNavigate && (
                        <button onClick={() => onNavigate('balirna', { tab: 'predpisy' })} className="text-[11px]"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor, padding: 0, textDecoration: 'underline' }}>
                            {data.predpis.kod} {data.predpis.nazev}
                        </button>
                    )}
                    {chyba && <span className="text-[11px]" style={{ color: '#EF4444' }}>{chyba}</span>}
                    <span className="flex-1" />
                    <button onClick={ulozit} disabled={!zmeneno || uklada}
                        className="flex items-center gap-1.5 text-xs font-semibold rounded-lg"
                        style={{
                            padding: '6px 12px', border: 'none', cursor: zmeneno ? 'pointer' : 'default',
                            background: zmeneno ? 'var(--accent, #F59E0B)' : borderColor, color: zmeneno ? '#fff' : mutedColor,
                        }}>
                        {uklada ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Uložit balné
                    </button>
                </div>
            </div>
        </div>
    );
}
